/**
 * App-shell chrome state — currently just the sidebar's collapsed mode.
 *
 * Lives at `src/store/` rather than inside `features/consultation-room/` on
 * purpose: `components/Layout.tsx` is the app shell and must not import from a
 * feature. The room still reports itself here (see AppRailCollapsed.tsx);
 * Layout reads the answer and the doctor drives it via the rail's own toggle.
 *
 * The rail starts collapsed every time the portal opens — the dashboard and
 * every other page open with the 60px icon rail, no click needed (product
 * decision, 8 Oct 2026). The doctor can expand it; that holds on every route,
 * the consultation room included, until she collapses it again or the portal
 * is reloaded. Nothing is persisted: a remembered "expanded" from an earlier
 * visit must not override the collapsed start.
 */
import { createStore } from './createStore';

interface AppShellState {
  /** A patient/consultation surface is open. Kept for the room's bookkeeping;
   *  it no longer changes the width, since the rail is collapsed unless the
   *  doctor expanded it — in or out of the room. */
  autoCollapsed: boolean;
  /** The doctor pressed Expand menu during this visit. */
  expanded: boolean;
}

const store = createStore<AppShellState>({ autoCollapsed: false, expanded: false });

/** True when the sidebar should render as the 60px icon rail. */
export function useAppRailCollapsed(): boolean {
  return !store.use(s => s.expanded);
}

export function setAppRailAutoCollapsed(value: boolean): void {
  store.setState({ autoCollapsed: value });
}

/** The rail's toggle, collapse side. */
export function collapseAppRail(): void {
  store.setState({ expanded: false });
}

/** The rail's toggle, expand side. Holds inside the room too. */
export function expandAppRail(): void {
  store.setState({ expanded: true });
}
